'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { Guidance, ISetParams } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchGuidance } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { ICalendarProps, IDateRange } from '../lib/interfaces';

export const fetchGuidanceData = fetchGuidance;
export const GuidanceCalendarColumnsDef = ColumnsDefinition;

export const GuidanceCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  noRowsOverlayText,
  onReady,
}) => {
  const session = React.useContext(SessionContext);
  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(DateTime.now().minus({ months: 1 }).toJSDate()),
    date_to: formatRequestDate(new Date()),
  };

  const initialParams = React.useMemo(
    () => ({
      dateFrom: dateRange.date_from,
      dateTo: dateRange.date_to,
      ...(initialTicker && { symbols: initialTicker }),
    }),
    [dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch({
    fetchPage: (params, page) =>
      fetchGuidanceData(session.getSession(), {
        ...(params as ISetParams),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData, initialParams);
    } else {
      onFilterChanged(initialParams);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      noRowsOverlayText={noRowsOverlayText}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
